import React, { Children, cloneElement, lazy, isValidElement } from 'react';
import { motion } from 'framer-motion';
interface ArticleSectionProps {
  eyebrow?: string;
  title?: string;
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
  image?: {
    src: string;
    alt: string;
    caption?: string;
  };
}
// Split a string into sentences while protecting common abbreviations
// so things like "Dr. Tracey" or "p<0.001" don't get incorrectly broken.
function splitSentences(text: string): string[] {
  if (!text) return [];
  const placeholders: Record<string, string> = {};
  let counter = 0;
  const abbrevPattern = /\b(Dr|Mr|Mrs|Ms|Jr|Sr|St|vs|etc|No|p|i\.e|e\.g|U\.S|U\.K)\./g;
  const protectedText = text.replace(abbrevPattern, m => {
    const key = `__ABBR${counter++}__`;
    placeholders[key] = m;
    return key;
  });
  const parts = protectedText.split(/(?<=[.!?])\s+(?=[A-Z"'])/g);
  return parts.map(p => {
    let restored = p;
    Object.entries(placeholders).forEach(([k, v]) => {
      restored = restored.split(k).join(v);
    });
    return restored.trim();
  }).filter(Boolean);
}
// Walk the article children, find <p> elements, and convert each text run
// inside them into a series of block-level spans — one per sentence —
// so the eye gets a clear visual beat between sentences.
function renderWithSentenceSpacing(children: ReactNode): ReactNode {
  return Children.map(children, child => {
    if (!isValidElement(child)) return child;
    if (child.type !== 'p') return child;
    const props = child.props as {
      children?: ReactNode;
    };
    const inner = Children.toArray(props.children).flatMap((node, i) => {
      if (typeof node === 'string') {
        const sentences = splitSentences(node);
        if (sentences.length <= 1) return [node];
        return sentences.map((s, j) => <span key={`s-${i}-${j}`} className="block mb-2 last:mb-0" data-id="element-102">
            {s}
          </span>);
      }
      return [node];
    });
    return cloneElement(child, {
      ...props,
      children: inner
    });
  });
}
export function ArticleSection({
  eyebrow,
  title,
  children,
  className = '',
  fullWidth = false,
  image
}: ArticleSectionProps) {
  return <motion.section className={`py-12 sm:py-16 ${className}`} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6
  }} data-id="element-103">
      <div className={`${fullWidth ? 'max-w-5xl' : 'max-w-article'} mx-auto px-4`} data-id="element-104">
        {(eyebrow || title) && <div className="mb-8" data-id="element-105">
            {eyebrow && <span className="block text-[11px] font-bold uppercase tracking-widest text-sage mb-4" data-id="element-106">
                {eyebrow}
              </span>}
            {title && <h2 className="text-3xl sm:text-4xl font-serif font-medium text-forest leading-tight" data-id="element-107">
                {title}
              </h2>}
          </div>}

        {image && <figure className="mb-10 -mx-4 sm:mx-0" data-id="element-108">
            <div className="rounded-none sm:rounded-2xl overflow-hidden shadow-sm bg-beige/40" data-id="element-109">
              <img src={image.src} alt={image.alt} className="w-full h-[260px] sm:h-[360px] md:h-[420px] object-cover" loading="lazy" data-id="element-110" />
            </div>
            {image.caption && <figcaption className="text-xs sm:text-sm text-ink/50 italic mt-3 px-4 sm:px-0 text-center" data-id="element-111">
                {image.caption}
              </figcaption>}
          </figure>}

        <div className="prose max-w-none prose-a:text-forest [&_p]:text-[1.375rem] sm:[&_p]:text-[1.625rem] [&_p]:leading-[1.55] [&_p]:text-ink/80 [&_p]:my-7" data-id="element-112">
          {renderWithSentenceSpacing(children)}
        </div>
      </div>
    </motion.section>;
}